import { useState } from 'react';
import { ErroApi } from '../lib/api';
import { CORES_STATUS, NOME_STATUS, responderEscala } from '../lib/escalas';
import { horaSP } from '../lib/datas';
import {
  lerFuncao,
  salvarDiaCulto,
  statusDoDia,
  type Demanda,
  type DiaCulto,
  type EquipeCulto,
  type EscaladoCulto,
  type EventoCulto,
  type Turno,
  type TurnoCulto,
} from '../lib/escalaCultos';
import { Botao } from './Interface';

type Pedido = {
  titulo: string;
  texto: string;
  confirmar: string;
  campo?: { rotulo: string; sugestao?: string; obrigatorio?: boolean };
  perigo?: boolean;
};

/**
 * Um dia de culto na escala: qual equipe cuida de cada demanda e quem (o 12 e
 * o ADM dessas equipes) ja confirmou. O admin monta ali mesmo. No domingo sao
 * dois cultos (10h e 19h), cada um com a sua escala: o cartao mostra e monta um
 * bloco para cada, e quem serve nos dois confirma cada culto.
 */

/** Um pedaco do cartao: o dia inteiro (terca, sabado) ou um dos cultos de domingo. */
type Bloco = {
  chave: 'dia' | Turno;
  titulo: string | null;
  demandas: { demanda: string; equipes: EquipeCulto[] }[];
  cultos: EventoCulto[];
};

type Rascunho = Record<string, Record<string, number[]>>;

/** Junta as linhas da mesma pessoa (ela pode estar em mais de um culto do bloco). */
function pessoasDosCultos(cultos: EventoCulto[]) {
  const porPessoa = new Map<number, { nome: string; funcao: string; linhas: EscaladoCulto[] }>();
  for (const c of cultos) {
    for (const p of c.escalados) {
      const atual = porPessoa.get(p.membroId) ?? { nome: p.nome, funcao: p.funcao, linhas: [] };
      atual.linhas.push(p);
      porPessoa.set(p.membroId, atual);
    }
  }
  return [...porPessoa.entries()];
}

export default function CartaoDiaCulto({
  dia,
  demandas,
  turnos,
  equipes,
  podeEditar,
  meuMembroId,
  somenteLeitura = false,
  perguntar,
  aoMudar,
  aoErrar,
  aoAvisar,
  semMoldura = false,
}: {
  dia: DiaCulto;
  demandas: Demanda[];
  /** os cultos de domingo (10h e 19h) */
  turnos: TurnoCulto[];
  equipes: EquipeCulto[];
  podeEditar: boolean;
  meuMembroId: number | null;
  /** dias que ja passaram: nada de confirmar */
  somenteLeitura?: boolean;
  perguntar: (p: Pedido) => Promise<{ ok: boolean; texto: string }>;
  aoMudar: () => Promise<void>;
  aoErrar: (mensagem: string) => void;
  aoAvisar: (mensagem: string) => void;
  semMoldura?: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState<Rascunho>({});
  const [ocupado, setOcupado] = useState<string | null>(null);

  const ehDomingo = dia.culto.chave === 'domingo' && turnos.length > 0;

  // domingo: um bloco por culto, cada um com as equipes do turno dele (equipe
  // sem turno serve nos dois). Terca e sabado: um bloco so, o dia inteiro.
  const blocos: Bloco[] = ehDomingo
    ? turnos.map((t) => ({
        chave: t.chave,
        titulo: `Culto das ${t.nome}`,
        demandas: dia.demandas
          .map((a) => ({ demanda: a.demanda, equipes: a.equipes.filter((e) => !e.turno || e.turno === t.chave) }))
          .filter((a) => a.equipes.length > 0),
        cultos: dia.cultos.filter((c) => c.turno === t.chave),
      }))
    : [{ chave: 'dia', titulo: null, demandas: dia.demandas, cultos: dia.cultos }];

  const equipesDe = (b: Bloco, chave: string) => b.demandas.find((d) => d.demanda === chave)?.equipes ?? [];

  function abrirEdicao() {
    setRascunho(
      Object.fromEntries(
        blocos.map((b) => [
          b.chave,
          Object.fromEntries(demandas.map((d) => [d.chave, equipesDe(b, d.chave).map((e) => e.id)])),
        ]),
      ),
    );
    setEditando(true);
  }

  const mudarRascunho = (bloco: string, demanda: string, ids: number[]) =>
    setRascunho((r) => ({ ...r, [bloco]: { ...(r[bloco] ?? {}), [demanda]: ids } }));

  async function executar(chave: string, acao: () => Promise<unknown>, falha: string) {
    setOcupado(chave);
    try {
      await acao();
      await aoMudar();
    } catch (e) {
      aoErrar(e instanceof ErroApi ? e.message : falha);
    } finally {
      setOcupado(null);
    }
  }

  async function salvar() {
    await executar(
      'salvar',
      async () => {
        const r = await salvarDiaCulto(
          dia.dia,
          ehDomingo
            ? { turnos: { manha: rascunho.manha ?? {}, noite: rascunho.noite ?? {} } }
            : { demandas: rascunho.dia ?? {} },
        );
        setEditando(false);
        const s = r.sincronia;
        aoAvisar(
          s.cultos === 0
            ? 'Salvo. O culto desse dia ainda não está na agenda: quando entrar, o 12 e o ADM das equipes entram sozinhos.'
            : `Salvo. ${s.entraram} entraram e ${s.sairam} saíram da escala.${
                s.semConta.length ? ` Sem conta (não foram avisados): ${s.semConta.join(', ')}.` : ''
              }`,
        );
      },
      'Não consegui salvar a escala.',
    );
  }

  // o resumo do cabecalho conta cada pessoa uma vez, mesmo servindo nos dois cultos
  const pessoas = pessoasDosCultos(dia.cultos);
  const confirmados = pessoas.filter(([, p]) => statusDoDia(p.linhas) === 'confirmado').length;

  async function responder(linhas: EscaladoCulto[], status: 'confirmado' | 'indisponivel') {
    let justificativa: string | undefined;
    if (status === 'indisponivel') {
      const r = await perguntar({
        titulo: 'Não vai poder?',
        texto: 'O administrador fica sabendo para acertar a escala.',
        confirmar: 'Avisar',
        campo: { rotulo: 'Motivo (opcional)' },
      });
      if (!r.ok) return;
      justificativa = r.texto.trim() || undefined;
    }
    await executar(
      `responder-${status}`,
      async () => {
        for (const l of linhas) await responderEscala(l.id, status, justificativa);
      },
      status === 'confirmado' ? 'Não consegui confirmar.' : 'Não consegui avisar.',
    );
  }

  const horarios = dia.cultos.map((c) => (c.inicio ? horaSP(c.inicio) : '')).filter(Boolean);
  const vazio = dia.demandas.length === 0;

  return (
    <div className={semMoldura ? '' : 'rounded-2xl bg-white p-4 shadow-cartao'}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-bold uppercase tracking-wide text-tinta-900">{dia.culto.nome}</h3>
          <p className="text-sm text-tinta-400">
            {horarios.length ? horarios.join(' e ') : 'culto ainda não está na agenda'}
            {pessoas.length > 0 && ` · ${confirmados}/${pessoas.length} confirmados`}
          </p>
        </div>
        {podeEditar && !editando && (
          <button
            onClick={abrirEdicao}
            className="shrink-0 rounded-lg border border-tinta-200 px-3 py-1.5 text-xs font-semibold text-tinta-700 hover:bg-tinta-100"
          >
            {vazio ? 'Montar escala' : 'Editar escala'}
          </button>
        )}
      </div>

      {editando ? (
        <div className="mt-3 space-y-4 rounded-xl bg-tinta-100 p-3">
          {blocos.map((b) => (
            <div key={b.chave} className="space-y-2">
              {b.titulo && <p className="text-xs font-bold uppercase tracking-wide text-tinta-600">{b.titulo}</p>}
              {demandas.map((d) => {
                const escolhidas = rascunho[b.chave]?.[d.chave] ?? [];
                const livres = equipes.filter((e) => !escolhidas.includes(e.id));
                return (
                  <div key={d.chave} className="flex flex-wrap items-center gap-2">
                    <span className="w-40 shrink-0 text-sm font-semibold text-tinta-800">{d.nome}</span>
                    {escolhidas.map((id) => (
                      <span
                        key={id}
                        className="flex items-center gap-1 rounded-full bg-tinta-900 px-3 py-0.5 text-sm text-white"
                      >
                        {equipes.find((e) => e.id === id)?.nome ?? '?'}
                        <button
                          onClick={() => mudarRascunho(b.chave, d.chave, escolhidas.filter((x) => x !== id))}
                          className="text-white/60 hover:text-white"
                          aria-label="Tirar equipe"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    {livres.length > 0 && (
                      <select
                        value=""
                        onChange={(ev) => {
                          const id = Number(ev.target.value);
                          if (id) mudarRascunho(b.chave, d.chave, [...escolhidas, id]);
                        }}
                        aria-label={`Equipe para ${d.nome}${b.titulo ? ` no ${b.titulo}` : ''}`}
                        className="rounded-lg border border-tinta-200 bg-white px-2 py-1 text-sm text-tinta-600"
                      >
                        <option value="">+ equipe</option>
                        {livres.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.nome}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <Botao carregando={ocupado === 'salvar'} onClick={() => void salvar()}>
              Salvar
            </Botao>
            <Botao variante="secundario" onClick={() => setEditando(false)}>
              Cancelar
            </Botao>
          </div>
        </div>
      ) : vazio ? (
        <p className="mt-3 text-sm text-tinta-400">
          {podeEditar ? 'Nenhuma equipe escalada ainda.' : 'A escala deste culto ainda não foi montada.'}
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          {blocos.map((b) => {
            const doBloco = pessoasDosCultos(b.cultos);
            return (
              <div key={b.chave}>
                {b.titulo && (
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-tinta-600">{b.titulo}</p>
                )}
                {b.demandas.length === 0 ? (
                  <p className="text-sm text-tinta-400">Nenhuma equipe escalada neste culto.</p>
                ) : (
                  <dl className="divide-y divide-tinta-100 rounded-xl border border-tinta-200">
                    {demandas
                      .filter((d) => equipesDe(b, d.chave).length > 0)
                      .map((d) => (
                        <div key={d.chave} className="flex items-baseline justify-between gap-3 px-3 py-1.5 text-sm">
                          <dt className="text-tinta-400">{d.nome}</dt>
                          <dd className="text-right font-semibold text-tinta-900">
                            {equipesDe(b, d.chave)
                              .map((e) => e.nome)
                              .join(' / ')}
                          </dd>
                        </div>
                      ))}
                  </dl>
                )}

                {doBloco.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {doBloco.map(([membroId, p]) => {
                      const minha = meuMembroId !== null && membroId === meuMembroId;
                      const status = statusDoDia(p.linhas);
                      const { papel, detalhe } = lerFuncao(p.funcao);
                      const motivo = p.linhas.find((l) => l.justificativa)?.justificativa;
                      return (
                        <li
                          key={membroId}
                          className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${
                            minha ? 'bg-white ring-2 ring-tinta-900' : 'bg-tinta-100'
                          }`}
                        >
                          <span className="min-w-0 truncate font-medium text-tinta-800">
                            {p.nome}
                            {minha && <span className="ml-1 text-xs font-semibold text-tinta-400">(você)</span>}
                          </span>
                          <span className="min-w-0 truncate text-xs text-tinta-400">
                            {papel}
                            {detalhe ? ` · ${detalhe}` : ''}
                          </span>
                          {motivo && status === 'indisponivel' && (
                            <span className="min-w-0 truncate text-xs text-tinta-400">· {motivo}</span>
                          )}
                          <span
                            className={`ml-auto shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold ${CORES_STATUS[status] ?? ''}`}
                          >
                            {NOME_STATUS[status] ?? status}
                          </span>
                          {minha && !somenteLeitura && (
                            <span className="flex w-full gap-2 pb-1 pt-1 sm:w-auto sm:pb-0 sm:pt-0">
                              {status !== 'confirmado' && (
                                <button
                                  disabled={ocupado !== null}
                                  onClick={() => void responder(p.linhas, 'confirmado')}
                                  className="rounded-lg bg-tinta-900 px-3 py-1 text-xs font-semibold text-white hover:bg-tinta-700 disabled:opacity-50"
                                >
                                  Confirmar presença
                                </button>
                              )}
                              {status !== 'indisponivel' && (
                                <button
                                  disabled={ocupado !== null}
                                  onClick={() => void responder(p.linhas, 'indisponivel')}
                                  className="rounded-lg border border-tinta-200 bg-white px-3 py-1 text-xs font-semibold text-tinta-700 hover:bg-tinta-100 disabled:opacity-50"
                                >
                                  Não vou poder
                                </button>
                              )}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
